import { MonthlyReportFilters } from "@/components/reports/MonthlyReportFilters";
import { ReportTable } from "@/components/reports/ReportTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { GetOptions, MonthlyReport, QueryConstraint, ReportType } from "@/core";
import { useGetProducts, useMonthlyReportsPaginated } from "@/hooks";
import {
  applySortingToReports,
  exportMonthlyReportsToCsv,
  SortingState,
} from "@/utils/csv-export";
import { formatNumber } from "@/utils/formatting";
import { Calendar, Download, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { DateRange } from "react-day-picker";
import { functionsService } from "@/services/functions/functions-service";

interface MonthlyReportFiltersType {
  searchTerm: string;
  dateRange: DateRange | undefined;
  year: string;
  month: string;
  buyOrSell: string;
  productId: string | null;
}

export default function MonthlyReportsPage() {
  const [filters, setFilters] = useState<MonthlyReportFiltersType>({
    searchTerm: "",
    dateRange: undefined,
    year: "all",
    month: "all",
    buyOrSell: "both",
    productId: null,
  });
  const [tableSorting, setTableSorting] = useState<SortingState[]>([]);
  const [productReports, setProductReports] = useState<MonthlyReport[] | null>(null);
  const [isProductReportsLoading, setIsProductReportsLoading] = useState(false);
  const fetchProductReportsRef = useRef<NodeJS.Timeout | null>(null);

  // The search box filters locally, so a paged query would only ever match the
  // rows already loaded. Widen the page while searching so every report is
  // reachable without hammering "Load More".
  const pageSize = filters.searchTerm.trim() ? 10_000 : 20;

  const queryOptions: GetOptions = useMemo(() => {
    const constraints: QueryConstraint[] = [];

    // Server-side filters where possible
    constraints.push({
      field: "type",
      operator: "==",
      value: ReportType.MONTHLY,
    });

    if (filters.year !== "all") {
      const yearNum = parseInt(filters.year, 10);
      if (!Number.isNaN(yearNum)) {
        constraints.push({ field: "year", operator: "==", value: yearNum });
      }
    }

    if (filters.month !== "all") {
      const monthNum = parseInt(filters.month, 10);
      if (!Number.isNaN(monthNum)) {
        constraints.push({ field: "month", operator: "==", value: monthNum });
      }
    }

    if (filters.buyOrSell !== "both") {
      constraints.push({
        field: "buyOrSell",
        operator: "==",
        value: filters.buyOrSell,
      });
    }

    // Optional date range filter on dataAsOf
    if (filters.dateRange?.from) {
      constraints.push({
        field: "dataAsOf",
        operator: ">=",
        value: filters.dateRange.from,
      });
    }
    if (filters.dateRange?.to) {
      constraints.push({
        field: "dataAsOf",
        operator: "<=",
        value: filters.dateRange.to,
      });
    }

    return {
      queryConstraints: constraints,
      pagination: { limit: pageSize },
      orderBy: { field: "dataAsOf", direction: "desc" },
    };
  }, [filters, pageSize]);

  const monthlyData = useMonthlyReportsPaginated(queryOptions);
  const { data: productsData } = useGetProducts();
  console.log(monthlyData.error);

  const reports = useMemo(
    () => (monthlyData.data ? monthlyData.data.pages.flat() : []),
    [monthlyData.data],
  );

  const filteredReports = useMemo(() => {
    const sourceReports: MonthlyReport[] = (productReports !== null ? productReports : reports) as MonthlyReport[];
    if (!sourceReports) return [];

    return sourceReports.filter((report: MonthlyReport) => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        if (
          !report.topProductName?.toLowerCase().includes(searchLower) &&
          !report.id.toLowerCase().includes(searchLower) &&
          !report.monthKey.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      if (filters.dateRange?.from) {
        const reportDate = new Date(report.dataAsOf);
        const fromDate = filters.dateRange.from;
        const toDate = filters.dateRange.to || new Date();

        if (reportDate < fromDate || reportDate > toDate) {
          return false;
        }
      }

      if (filters.year !== "all" && report.year.toString() !== filters.year) {
        return false;
      }

      if (
        filters.month !== "all" &&
        report.month.toString() !== filters.month
      ) {
        return false;
      }

      // Always filter by transaction type: when 'both' is selected, only include 'both'
      if (report.buyOrSell !== filters.buyOrSell) {
        return false;
      }

      return true;
    });
  }, [reports, productReports, filters]);

  const handleRefresh = async () => {
    try {
      await monthlyData.refetch();
    } catch (error) {
      console.error("Failed to refresh reports:", error);
    }
  };

  const handleFiltersChange = (newFilters: MonthlyReportFiltersType) => {
    setFilters(newFilters);
  };

  const handleExport = () => {
    if (filteredReports.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Apply the same sorting as the table before exporting
    const sortedReports = applySortingToReports(filteredReports, tableSorting);
    exportMonthlyReportsToCsv(sortedReports, filters.buyOrSell as "buy" | "sell" | "both");
  };

  const handleGetMonthlyProductReportByProduct = useCallback(
    async (productId: string, year?: number, month?: number) => {
      const result = await functionsService.getMonthlyProductReportByProduct({
        productId,
        year,
        month,
        ...(filters.buyOrSell !== "both" ? { buyOrSell: filters.buyOrSell as "buy" | "sell" } : {}),
      });
      // Map/cast result to MonthlyReport
      return (result || []).map((d, idx) => {
        const data = d as Partial<MonthlyReport>;
        return {
          ...data,
          id: data.id || `monthly-prod-${year}-${month}-${idx}`,
          year: data.year ?? year,
          month: data.month ?? month,
          monthKey: data.monthKey || `${year}-${String(month).padStart(2, "0")}`,
          type: ReportType.MONTHLY,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as MonthlyReport;
      });
    },
    [filters.buyOrSell],
  );

  useEffect(() => {
    if (!filters.productId) {
      setProductReports(null);
      setIsProductReportsLoading(false);
      return;
    }
    const year = filters.year !== "all" ? parseInt(filters.year, 10) : undefined;
    const month = filters.month !== "all" ? parseInt(filters.month, 10) : undefined;
    if (fetchProductReportsRef.current) clearTimeout(fetchProductReportsRef.current);
    setIsProductReportsLoading(true);
    fetchProductReportsRef.current = setTimeout(async () => {
      try {
        const data = await handleGetMonthlyProductReportByProduct(
          filters.productId as string,
          year,
          month
        );
        setProductReports(data);
      } catch {
        setProductReports([]);
      } finally {
        setIsProductReportsLoading(false);
      }
    }, 650);
    return () => {
      if (fetchProductReportsRef.current) clearTimeout(fetchProductReportsRef.current);
    };
  }, [filters.productId, filters.year, filters.month, handleGetMonthlyProductReportByProduct]);

  if ((monthlyData.error || !reports) && !monthlyData.isLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Monthly Reports
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              There was an error loading the monthly reports data. Please try
              again.
            </p>
            <Button onClick={handleRefresh} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex items-center">
          <h2 className="text-2xl font-bold">Monthly Reports</h2>
          <h3 className="text-lg ml-2 text-muted-foreground self-start mt-1">
            ({formatNumber(filteredReports?.length || 0)})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={
              monthlyData.isLoading ||
              monthlyData.isFetching ||
              filteredReports.length === 0
            }
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={monthlyData.isLoading || monthlyData.isFetching}
            className="gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                monthlyData.isLoading || monthlyData.isFetching
                  ? "animate-spin"
                  : ""
              }`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-2">
        <MonthlyReportFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          isLoading={monthlyData.isLoading}
          products={(productsData || []).map((p) => ({ id: p.id, name: p.name }))}
        />
      </div>

      <ReportTable
        key="ReportTable-MONTHLY"
        selectedReportType={ReportType.MONTHLY}
        reports={filteredReports}
        isLoading={monthlyData.isLoading || isProductReportsLoading}
        onSortingChange={setTableSorting}
      />

      {monthlyData.hasNextPage && (
        <div className="flex items-center justify-center py-4">
          <Button
            variant="outline"
            onClick={() => monthlyData.fetchNextPage()}
            disabled={monthlyData.isFetchingNextPage}
          >
            {monthlyData.isFetchingNextPage ? (
              <>
                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                Loading more...
              </>
            ) : (
              "Load More"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
